import { createSelector } from '@ngrx/store';
import * as R from 'ramda';
import { AppState } from '../app.state';
import { PreviewFileType } from '@gelato-api-ui/core/preflight/preview-file-type.enum';
import {
  StoreProductPreviewsCollection,
  StoreProductVariantPreviewsCollection,
} from '@product-catalogue/src/lib/product-catalogue/services/store-product-variants.service';
import { key2SceneName } from '@gelato-api-ui/core/e-commerce/helpers/key2SceneName';
import {
  BulkConnectState,
  BulkConnectVariantsMappingPayload,
  DesignAssetCostsCollection,
  PriceCollection,
  SelectedPreviewScenesCollection,
  SelectedProductVariantKeysCollection,
  State,
} from './e-commerce-product-wizard.reducer';
import { EProduct } from '@gelato-api-ui/core/e-commerce/e-product';
import { EProductDetails } from '@gelato-api-ui/core/e-commerce/e-product-details';
import { EProductWizardMode } from '@gelato-api-ui/core/e-commerce/e-product-wizard-mode.enum';
import { CategoryDesignFamilyResponse } from '@gelato-api-ui/core/designs/category-design-family-response';
import { isApparelOrAccessoriesProductCategory } from '@product-catalogue/src/lib/ngrx/product-catalogue.reducer';
import { calculateTotalDesignAssetsCost } from './helpers/calculateTotalDesignAssetsCost';
import { EProductWithVariants } from '@gelato-api-ui/core/e-commerce/e-product-with-variants';
import { EProductVariant } from '@gelato-api-ui/core/e-commerce/e-product-variant';
import { EProductVariantConnectionStatus } from '@gelato-api-ui/core/e-commerce/e-product-variant-connection-status.enum';
import { ECommerceProductWizardStep } from '@gelato-api-ui/core/e-commerce/e-commerce-product-wizard-step.enum';
import { getCurrentTimeStamp } from '@gelato-api-ui/core/datetime/helpers/getCurrentTimeStamp';
import { MetadataItem } from '@gelato-api-ui/core/metadata/types/metadata-item';
import { EProductMetadataKey } from '@gelato-api-ui/core/e-commerce/e-product-metadata-key.enum';
import { EProductVariantsCollection } from '@gelato-api-ui/core/e-commerce/types/e-product-variants-collection';
import {
  CUSTOM_SCENE_TAG,
  PREMIUM_SCENE_TAG,
} from '@product-catalogue/src/lib/store-product-media/constants/store-product-media';
import { RecommendedRetailPrices } from '@product-catalogue/src/lib/product-catalogue/types/product-recommeded-retail-prices';
import { GarmentSizesSorted } from '@gelato-api-ui/core/garment/garment-sizes-sorted';
import {
  PriceNavigatorDraftPrices,
  PriceNavigatorItemModel,
} from '@api-ui-app/src/app/price-navigator/prices/types/price-navigator.types';
import { PriceNavigatorProductsMappingService } from '@api-ui-app/src/app/price-navigator/price-navigator-shared/services/price-navigator-products-mapping.service';
import { VariantModel } from '@api-ui-app/src/app/price-navigator/prices/types/price-navigator-product.types';
import { EStore } from '@gelato-api-ui/core/e-commerce/e-store';

export const getState = (state: AppState): State => state.eCommerceProductWizard;

export const getDestinationProductId = createSelector(getState, (state: State): string => state.destinationProductId);

export const getDestinationProduct = createSelector(
  getState,
  (state: State): EProductWithVariants => state.destinationProduct,
);

export const getDestinationProductVariants = createSelector(
  getState,
  (state: State): EProductVariant[] => (state.destinationProduct ? state.destinationProduct.variants : null) || [],
);

export const getBulkConnectState = createSelector(getState, (state: State): BulkConnectState => state.bulkConnect);

export const getBulkConnectDestinationProductVariants = createSelector(
  getDestinationProductVariants,
  (destinationProductVariants: EProductVariant[]): EProductVariant[] =>
    destinationProductVariants.filter(
      (loopProductVariant: EProductVariant) =>
        loopProductVariant.connectionStatus === EProductVariantConnectionStatus.NOT_CONNECTED,
    ),
);

export const getBulkConnectDefaultProductVariantsMappingIsLoading = createSelector(
  getBulkConnectState,
  (bulkConnectState: BulkConnectState): boolean => bulkConnectState.defaultMapping.isLoading,
);

export const getBulkConnectDefaultProductVariantsMappingPayload = createSelector(
  getBulkConnectState,
  (bulkConnectState: BulkConnectState): BulkConnectVariantsMappingPayload => bulkConnectState.defaultMapping.payload,
);

export const getBulkConnectCustomProductVariantsMappingPayload = createSelector(
  getBulkConnectState,
  (bulkConnectState: BulkConnectState): BulkConnectVariantsMappingPayload => bulkConnectState.customMapping,
);

export const getBulkConnectMappedProductVariantsCount = createSelector(
  getBulkConnectCustomProductVariantsMappingPayload,
  (mappingPayload: BulkConnectVariantsMappingPayload): number =>
    Object.keys(mappingPayload).filter((key: string) => Boolean(mappingPayload[key])).length,
);

export const getBulkConnectAutomaticallyConnectedProductVariantsCount = createSelector(
  getBulkConnectDefaultProductVariantsMappingPayload,
  getBulkConnectCustomProductVariantsMappingPayload,
  (defaultMapping: BulkConnectVariantsMappingPayload, customMapping: BulkConnectVariantsMappingPayload): number => {
    const defaultMappingParis = R.toPairs(defaultMapping);
    const customMappingPairs = R.toPairs(customMapping);

    return R.intersection(customMappingPairs, defaultMappingParis).filter(pair => Boolean(pair[1])).length;
  },
);

export const getBulkConnectManuallyConnectedProductVariantsCount = createSelector(
  getBulkConnectDefaultProductVariantsMappingPayload,
  getBulkConnectCustomProductVariantsMappingPayload,
  (defaultMapping: BulkConnectVariantsMappingPayload, customMapping: BulkConnectVariantsMappingPayload): number => {
    const defaultMappingParis = R.toPairs(defaultMapping);
    const customMappingPairs = R.toPairs(customMapping);

    return R.difference(customMappingPairs, defaultMappingParis).filter(pair => Boolean(pair[1])).length;
  },
);
export const getBulkConnectCreatePreviewFlag = createSelector(
  getBulkConnectState,
  (bulkConnectState: BulkConnectState): boolean => bulkConnectState.createPreviews,
);

export const getProduct = createSelector(getState, (state: State): EProduct => state.product);

export const getProductTitle = createSelector(getProduct, (product: EProduct): string => product?.title);

export const getProductDescription = createSelector(getProduct, (product: EProduct): string => product?.description);

export const getProductMetadata = createSelector(getProduct, (product: EProduct): MetadataItem[] => product?.metadata);

export const getProductTemplateName = createSelector(getProduct, (product: EProduct): string => product?.templateName);

export const getProductDetails = createSelector(
  getState,
  (state: State): EProductDetails => ({
    product: state.product,
    attachSizeGuideCmTable: state.attachSizeGuideCmTable,
    attachSizeGuideInchTable: state.attachSizeGuideInchTable,
    attachProductCareInstructions: state.attachProductCareInstructions,
    publishImmediately: state.publishImmediately,
    publishWithFreeShipping: state.publishWithFreeShipping,
    attachIllustrationSteps: state.attachIllustrationSteps,
    hasFlatShippingPriceType: state.productDetails?.hasFlatShippingPriceType,
  }),
);

const getSelectedProductVariantKeys = createSelector(
  getState,
  (state: State): SelectedProductVariantKeysCollection => state.selectedProductVariantKeys,
);

const getProductVariantKeysArray = createSelector(getState, (state: State): string[] => state.productVariantKeysArray);

export const getDefaultProductUid = createSelector(getState, (state: State): string => state.defaultProductUid);

export const getDefaultPageCount = createSelector(getState, (state: State): number => state.defaultPageCount);

export const getPageCount = createSelector(getState, (state: State): number => state.pageCount);

export const getSelectedProductVariantKeysCollection = createSelector(
  isApparelOrAccessoriesProductCategory,
  getSelectedProductVariantKeys,
  getProductVariantKeysArray,
  (
    apparelOrAccessoriesProductCategory: boolean,
    selectedProductVariantKeys: SelectedProductVariantKeysCollection,
    productVariantKeysArray: string[],
  ): SelectedProductVariantKeysCollection => {
    if (apparelOrAccessoriesProductCategory) {
      return productVariantKeysArray.reduce(
        (
          acc: SelectedProductVariantKeysCollection,
          productVariantKey: string,
        ): SelectedProductVariantKeysCollection => ({ ...acc, [productVariantKey]: true }),
        {},
      );
    }

    return selectedProductVariantKeys;
  },
);

export const getSelectedProductVariantKeysArray = createSelector(
  getSelectedProductVariantKeysCollection,
  (selectedProductVariantKeysCollection: SelectedProductVariantKeysCollection) =>
    Object.keys(selectedProductVariantKeysCollection).reduce((acc: string[], productVariantKey: string): string[] => {
      if (selectedProductVariantKeysCollection[productVariantKey]) {
        return [...acc, productVariantKey];
      }

      return acc;
    }, []),
);

export const getStoreProductVariantsCollection = createSelector(
  getState,
  (state: State): EProductVariantsCollection => state.storeProductVariants,
);

export const getOriginalStoreProductVariantsCollection = createSelector(
  getState,
  (state: State): EProductVariantsCollection => state.originalStoreProductVariants,
);

export const getSelectedPreviewFileType = createSelector(
  getState,
  (state: State): PreviewFileType => state.selectedPreviewFileType,
);

// To quickly fix the issue(GAPI-16328) duplicated scenes were just filtered
// TODO: To remove duplicated scenes,
// where different parts of the application rely on the supportedPreviewScenes property,
// it is ideal to keep separate supported preview scenes for each module.
export const getSupportedPreviewScenes = createSelector(getState, (state: State) =>
  R.uniqBy(R.prop('id'))(state.supportedPreviewScenes || []),
);
export const getSupportedPreviewSceneIds = createSelector(
  getSupportedPreviewScenes,
  scenes => scenes?.map(scene => scene.id) || [],
);
export const getSupportedPreviewScenesMap = createSelector(getSupportedPreviewScenes, scenes =>
  scenes?.reduce((acc, scene) => ({ ...acc, [scene.id]: scene }), {}),
);

export const getSelectedPreviewScenes = createSelector(
  getState,
  (state: State): SelectedPreviewScenesCollection => state?.selectedPreviewScenes || {},
);

export const productMockupsMetadata = createSelector(
  getSelectedPreviewScenes,
  getSupportedPreviewScenesMap,
  (scenes, supportedScenes) => {
    const selectedScenes = Object.entries(scenes || {})
      .filter(([_, isSelected]) => isSelected)
      .map(([sceneId]) => sceneId);
    const scenesTypes = selectedScenes?.map(sceneId => {
      const premium = supportedScenes?.[sceneId]?.tags?.find(tag => tag === PREMIUM_SCENE_TAG);
      const custom = supportedScenes?.[sceneId]?.tags?.find(tag => tag === CUSTOM_SCENE_TAG);

      return premium || custom;
    });
    const metadata: MetadataItem[] = [...new Set(scenesTypes)].map(sceneType => {
      const usedStandardMockup = { key: EProductMetadataKey.USED_STANDARD_MOCKUP, value: '1' };
      const sceneTypeConfig = {
        [PREMIUM_SCENE_TAG]: { key: EProductMetadataKey.USED_PREMIUM_MOCKUP, value: '1' },
        [CUSTOM_SCENE_TAG]: { key: EProductMetadataKey.USED_CUSTOM_MOCKUP, value: '1' },
      };

      return sceneTypeConfig[sceneType] || usedStandardMockup;
    });

    return metadata;
  },
);

export const getSelectedPreviewScenesArray = createSelector(
  getSelectedPreviewScenes,
  (selectedPreviewScenes: SelectedPreviewScenesCollection): string[] => {
    if (!selectedPreviewScenes) {
      return [];
    }

    const selectedPreviewScenesArray = [];

    R.keys(selectedPreviewScenes).forEach((key: string) => {
      if (selectedPreviewScenes[key]) {
        selectedPreviewScenesArray.push(key2SceneName(key));
      }
    });

    return selectedPreviewScenesArray;
  },
);

export const getSelectedPreviewScenesCount = createSelector(
  getSelectedPreviewScenesArray,
  (selectedPreviewScenesArray: string[]): number => selectedPreviewScenesArray.length,
);

export const getPrimaryPreviewScene = createSelector(getState, (state: State): string => state.primaryPreviewScene);

export const getPrimaryPreviewProductVariantKey = createSelector(
  getState,
  (state: State): string => state.primaryPreviewProductVariantKey,
);

export const getAttachSizeGuideCmTableFlag = createSelector(
  getState,
  (state: State): boolean => state.attachSizeGuideCmTable,
);

export const getAttachSizeGuideInchTableFlag = createSelector(
  getState,
  (state: State): boolean => state.attachSizeGuideInchTable,
);

export const getAttachSizeGuideFlag = createSelector(
  getAttachSizeGuideCmTableFlag,
  getAttachSizeGuideInchTableFlag,
  (attachSizeGuideCmTable: boolean, attachSizeGuideInchTable: boolean): boolean =>
    attachSizeGuideCmTable || attachSizeGuideInchTable,
);

export const getAttachProductCareInstructionsFlag = createSelector(
  getState,
  (state: State): boolean => state.attachProductCareInstructions,
);

export const getActivePriceFetchingRequestsCount = createSelector(
  getState,
  (state: State): number => state.activePriceFetchingRequestsCount,
);

export const isFetchingProductCosts = createSelector(
  getActivePriceFetchingRequestsCount,
  (activePriceFetchingRequestsCount: number): boolean => activePriceFetchingRequestsCount > 0,
);

export const getStoreProductVariantByProductUid = (productUid: string) =>
  createSelector(getStoreProductVariantsCollection, collection => collection[productUid]);

export const getPublishImmediatelyFlag = createSelector(getState, (state: State): boolean => state.publishImmediately);

export const getPublishWithFreeShippingFlag = createSelector(
  getState,
  (state: State): boolean => state.publishWithFreeShipping,
);

export const getProductPreviewsCollection = createSelector(
  getState,
  (state: State): StoreProductPreviewsCollection => state.productPreviewsCollection,
);

export const getProductVariantPreviewsCollection = createSelector(
  getState,
  (state: State): StoreProductVariantPreviewsCollection => state.productVariantPreviewsCollection,
);

export const getProductCosts = createSelector(getState, (state: State): PriceCollection => state.productCosts);

export const getProductShippingCosts = createSelector(
  getState,
  (state: State): PriceCollection => state.productShippingCosts,
);

export const getProductCostsForSelectedProductVariantKeys = createSelector(
  getProductCosts,
  getSelectedProductVariantKeysArray,
  (productCosts: PriceCollection, selectedProductVariantKeysArray: string[]): PriceCollection =>
    R.pick(selectedProductVariantKeysArray, productCosts),
);

export const getProductShippingCostsForSelectedProductVariantKeys = createSelector(
  getProductShippingCosts,
  getSelectedProductVariantKeysArray,
  (productCosts: PriceCollection, selectedProductVariantKeysArray: string[]): PriceCollection =>
    R.pick(selectedProductVariantKeysArray, productCosts),
);

export const getDesignAssetCosts = createSelector(
  getState,
  (state: State): DesignAssetCostsCollection => state.designAssetCosts,
);

export const getRecommendedRetailPrices = createSelector(
  getState,
  (state: State): RecommendedRetailPrices[] => state.recommendedRetailPrices,
);

export const getDesignAssetCostsForSelectedProductVariantKeys = createSelector(
  getDesignAssetCosts,
  getSelectedProductVariantKeysArray,
  (
    designAssetCosts: DesignAssetCostsCollection,
    selectedProductVariantKeysArray: string[],
  ): DesignAssetCostsCollection => R.pick(selectedProductVariantKeysArray, designAssetCosts),
);

export const getDesignTemplateCosts = createSelector(
  getState,
  (state: State): PriceCollection => state.designTemplateCosts,
);

export const getDesignTemplateCostsForSelectedProductVariantKeys = createSelector(
  getDesignTemplateCosts,
  getSelectedProductVariantKeysArray,
  (designTemplateCosts: PriceCollection, selectedProductVariantKeysArray: string[]): PriceCollection =>
    R.pick(selectedProductVariantKeysArray, designTemplateCosts),
);

export const getTotalCosts = createSelector(
  getProductCosts,
  getDesignAssetCosts,
  getDesignTemplateCosts,
  (
    productCosts: PriceCollection,
    designAssetCosts: DesignAssetCostsCollection,
    designTemplateCosts: PriceCollection,
  ): PriceCollection =>
    Object.keys(productCosts).reduce(
      (acc: PriceCollection, productUid: string) => ({
        ...acc,
        [productUid]:
          (productCosts[productUid] || 0) +
          calculateTotalDesignAssetsCost(designAssetCosts[productUid]) +
          (designTemplateCosts[productUid] || 0),
      }),
      {},
    ),
);

export const getTotalCostsForSelectedProductVariantKeys = createSelector(
  getState,
  getTotalCosts,
  getSelectedProductVariantKeysArray,
  getBulkConnectCustomProductVariantsMappingPayload,
  (
    state: State,
    totalCosts: PriceCollection,
    selectedProductVariantKeysArray: string[],
    mapping: BulkConnectVariantsMappingPayload,
  ): PriceCollection => {
    const usedProductVariantKeys = Object.values(mapping || {});
    const productVariantKeys =
      state.mode === EProductWizardMode.BULK_CONNECT
        ? selectedProductVariantKeysArray.filter((loopProductVariantKey: string): boolean =>
            usedProductVariantKeys.includes(loopProductVariantKey),
          )
        : selectedProductVariantKeysArray;

    return R.pick(productVariantKeys, totalCosts);
  },
);

export const getTotalCostsArrayForSelectedProductVariantKeys = createSelector(
  getTotalCostsForSelectedProductVariantKeys,
  (totalCosts: PriceCollection): number[] => Object.values(totalCosts),
);

export const getPrices = createSelector(getState, (state: State): PriceCollection => state.prices);

export const getPricesForSelectedProductVariantKeys = createSelector(
  getPrices,
  getSelectedProductVariantKeysArray,
  (prices: PriceCollection, selectedProductVariantKeysArray: string[]): PriceCollection =>
    R.pick(selectedProductVariantKeysArray, prices),
);

export const isFetchingDesignAssetCosts = createSelector(
  getState,
  (state: State): boolean => state.fetchingDesignAssetCosts,
);

export const isFetchingDesignTemplateCosts = createSelector(
  getState,
  (state: State): boolean => state.fetchingDesignTemplateCosts,
);

export const getDesignFamily = createSelector(
  getState,
  (state: State): CategoryDesignFamilyResponse => state.designFamily,
);

export const getMode = createSelector(getState, (state: State): EProductWizardMode => state.mode);

export const getIsTemplateMode = createSelector(getMode, (mode: EProductWizardMode): boolean =>
  [EProductWizardMode.CREATE_PRODUCT_TEMPLATE, EProductWizardMode.EDIT_PRODUCT_TEMPLATE].includes(mode),
);

export const getStartedAt = createSelector(getState, (state: State): number => state.startedAt);

export const getDuration = createSelector(
  getStartedAt,
  (startedAt: number): number => (getCurrentTimeStamp() - startedAt) / 1000,
);

export const isSavingDraft = createSelector(getState, (state: State): boolean => state.isSavingDraft);

export const getHasDraft = createSelector(getState, (state: State): boolean => state.product?.hasDraft);

export const getLatestSavedDraftHash = createSelector(getState, (state: State): string => state.latestSavedDraftHash);

export const getActiveStep = createSelector(getState, (state: State): ECommerceProductWizardStep => state.activeStep);

export const getProductVariantKeysArrayForPreviewDownload = createSelector(
  isApparelOrAccessoriesProductCategory,
  getSelectedProductVariantKeysArray,
  (isApparel, productUids): string[] => {
    if (isApparel) {
      // filtering apparel sizes as the previews are similar for all sizes
      const collection = {};

      productUids.forEach(productUid => {
        const valuesToReplace = GarmentSizesSorted.join('|');
        const key = productUid.replace(new RegExp(valuesToReplace, 'gi'), '');
        collection[key] = productUid;
      });
      return Object.values(collection);
    }
    return productUids;
  },
);

export const getDownloadPreviewsAmount = createSelector(
  getProductVariantKeysArrayForPreviewDownload,
  getSelectedPreviewScenesArray,
  (productVariantKeysArray, previewScenes) => productVariantKeysArray.length * previewScenes.length,
);

export const getIsSavingTemplate = createSelector(getState, (state: State): boolean => state.isSavingTemplate);

export const getSelectedPricesCountry = createSelector(getState, (state: State): string => state.selectedPricesCountry);

export const getSelectedStore = createSelector(getState, (state: State): EStore => state.selectedStore);

export const getSelectedPricesCurrency = createSelector(
  getState,
  (state: State): string => state.selectedPricesCurrency || state.selectedStore?.currency,
);

export const getProductPrices = createSelector(
  getState,
  (state: State): PriceNavigatorItemModel[] => state.productPrices,
);

export const getProductDraftPrices = createSelector(
  getState,
  (state: State): PriceNavigatorDraftPrices[] => state.draftPricesToChange,
);

export const getPricesTableData = createSelector(
  getProductPrices,
  getProductDraftPrices,
  getProductTitle,
  getStoreProductVariantsCollection,
  (
    initialPrices: PriceNavigatorItemModel[],
    draftPrices: PriceNavigatorDraftPrices[],
    productTitle: string,
    selectedVariants: EProductVariantsCollection,
  ): PriceNavigatorItemModel[] => {
    // Sync PN price variants with those selected in the editor. Necessary when deleting variants from the product
    const matchedPricesWithSelectedVariants = initialPrices.map((product: PriceNavigatorItemModel) => ({
      ...product,
      name: product.name || productTitle,
      variants: [
        ...product.variants
          .filter((variant: VariantModel) => !!selectedVariants[variant.productUid])
          .map((variant: VariantModel) => ({
            ...variant,
            title: selectedVariants[variant.productUid]?.title,
            productName: variant.productName || productTitle,
          })),
      ],
    }));

    return PriceNavigatorProductsMappingService.calculateProfitBasedOnNewPrices(
      matchedPricesWithSelectedVariants,
      draftPrices,
    );
  },
);

export const getIsProductPricesLoading = createSelector(
  getState,
  (state: State): boolean => state.isProductPricesLoading,
);

export const getStoreProductNewVariantsUids = createSelector(
  getStoreProductVariantsCollection,
  (selectedVariants: EProductVariantsCollection): string[] => {
    return Object.keys(selectedVariants).filter((uid: string) => !selectedVariants[uid].id);
  },
);
